import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let catalogPromise = null;

const useModuleInfo = (moduleKey) => {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        catalogPromise = catalogPromise || axios.get(`${API}/public/modules/catalog?tenant_type=airline`).then((r) => r.data.addons || []).catch(() => []);
        catalogPromise.then((addons) => setInfo(addons.find((a) => a.key === moduleKey) || null));
    }, [moduleKey]);
    return info;
};

export const ContractModuleButton = ({ moduleKey, size = 'sm', className = '' }) => {
    const { user } = useAuth();
    const info = useModuleInfo(moduleKey);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const isAdmin = user?.role === 'admin';

    const contract = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/billing/modules/${moduleKey}/contract`);
            if (r.data.status === 'enabled') {
                toast.success('Módulo activado. Recargando…');
                setTimeout(() => window.location.reload(), 800);
            } else {
                toast.success(r.data.duplicate ? 'Ya tienes una solicitud abierta para este módulo. El equipo de AeroOps te contactará.' : 'Solicitud enviada. El equipo de AeroOps te contactará para activarlo.');
                setSent(true);
            }
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo enviar la solicitud');
        } finally {
            setBusy(false);
        }
    };

    if (!isAdmin) {
        return <span className="text-xs text-muted-foreground" data-testid={`contract-ask-admin-${moduleKey}`}>Pídele a tu administrador que solicite la contratación</span>;
    }
    if (sent) {
        return <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400" data-testid={`contract-sent-${moduleKey}`}>Solicitud enviada</span>;
    }
    return (
        <>
            <Button size={size} className={`bg-amber-500 hover:bg-amber-600 text-black font-bold ${className}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }} data-testid={`contract-module-btn-${moduleKey}`}>
                Solicitar contratación
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent data-testid={`contract-dialog-${moduleKey}`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Solicitar {info?.label || moduleKey}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {info?.description}{' '}
                            {info && <strong className="text-foreground">USD {info.price_monthly_usd}/mes.</strong>}{' '}
                            Enviaremos tu solicitud al equipo de AeroOps, que te contactará para activarlo. No se hace ningún cobro automático.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="contract-cancel-btn">Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); contract(); }} disabled={busy} data-testid="contract-confirm-btn">
                            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Confirmar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export const ModuleLockedScreen = ({ moduleKey }) => {
    const info = useModuleInfo(moduleKey);
    return (
        <div className="max-w-2xl mx-auto py-16 text-center" data-testid="module-disabled-screen">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
                <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-3xl font-bold uppercase tracking-tight mb-3 font-['Barlow_Condensed']">{info?.label || 'Módulo'} no contratado</h1>
            <p className="text-muted-foreground mb-2">{info?.description || 'Este módulo no está incluido en el plan de tu aerolínea.'}</p>
            {info && <p className="text-sm mb-8"><span className="font-mono text-amber-500 font-bold">USD {info.price_monthly_usd}</span> <span className="text-muted-foreground">/ mes</span></p>}
            <div className="flex items-center justify-center gap-3">
                <ContractModuleButton moduleKey={moduleKey} size="default" />
                <Button variant="outline" onClick={() => window.location.assign('/dashboard')} data-testid="module-disabled-back-btn">Volver al Dashboard</Button>
            </div>
        </div>
    );
};
